import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  useCreateStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  useListMemberEntitlements,
  useListItemVariants,
  getListStockOutQueryKey,
  getListMemberEntitlementsQueryKey,
  getListItemVariantsQueryKey,
} from "@workspace/api-client-react";
import type { InventoryItemVariant } from "@workspace/api-client-react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ItemPicker } from "./item-picker";
import { MemberCombobox } from "./member-combobox";
import {
  StockOutModifierCheckboxes,
  type AppliedModifierEntry,
} from "@/components/inventory/stock-out-modifier-checkboxes";

// Phase 2.1 polish H — shared form for With-Limit + Pay-Per-Use stock-out.
// Items are filtered by the category's charging_model so the dropdown only
// shows items relevant to the selected mode. Operator must identify the
// consumer either by member or by seat/cabin number — seat-only is fine
// for day passes / visitors. Phase 3 (Member Billing) will layer the proper
// charging-decision auto-derive on top; for now we just record the row.

export type MemberStockOutMode = "with_limit" | "pay_per_use";

const MODE_CONFIG: Record<MemberStockOutMode, {
  title: string;
  hint: string;
  consumptionType: "Plan_Quota_Usage" | "Member_Paid_Use";
  chargingModels: string[];
  showAmount: boolean;
}> = {
  with_limit: {
    title: "Log With-Limit Usage",
    hint: "Stationery, prints, scans — items consumed against a member's plan quota or with-limit allowance.",
    consumptionType: "Plan_Quota_Usage",
    chargingModels: ["Complimentary_With_Limit", "Plan_Inclusive_Quota"],
    showAmount: false,
  },
  pay_per_use: {
    title: "Log Pay-Per-Use Charge",
    hint: "F&B, decor, add-ons — items charged on consumption.",
    consumptionType: "Member_Paid_Use",
    chargingModels: ["Pay_Per_Use", "AddOn_Optional"],
    showAmount: true,
  },
};

const MemberStockOutSchema = z.object({
  transactionDate: z.date({ required_error: "Pick a date" }),
  itemId: z.coerce.number().int().positive("Pick an item"),
  quantity: z.coerce.number().positive("Quantity must be > 0"),
  memberId: z.number().int().positive().nullable().optional(),
  seatNumber: z.string().nullable().optional(),
  amountCharged: z.coerce.number().nonnegative().nullable().optional(),
}).superRefine((data, ctx) => {
  const hasMember = data.memberId != null;
  const hasSeat = !!data.seatNumber && data.seatNumber.trim().length > 0;
  if (!hasMember && !hasSeat) {
    ctx.addIssue({ code: "custom", path: ["memberId"], message: "Pick a member OR enter a seat number" });
  }
});

type FormValues = z.infer<typeof MemberStockOutSchema>;

interface Props {
  mode: MemberStockOutMode;
  onSaved?: () => void;
  onDiscard?: () => void;
}

export function MemberStockOutForm({ mode, onSaved, onDiscard }: Props) {
  const cfg = MODE_CONFIG[mode];
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const { data: categories = [] } = useListInventoryCategories();
  const createMutation = useCreateStockOut();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Phase 6 — modifier + variant state
  // Phase 55 Plan 55-05 (RECIPE-MODIFIER-01): wire shape flipped from
  // `appliedModifierIds: number[]` to `appliedModifiers: AppliedModifierEntry[]`.
  // count-bearing entries let the counter UI represent modifier stacking
  // (e.g. Cheese ×3) end-to-end to the server.
  const [appliedModifiers, setAppliedModifiers] = useState<AppliedModifierEntry[]>([]);
  const [variantId, setVariantId] = useState<number | null>(null);
  const [lineTotal, setLineTotal] = useState<number | null>(null);
  const [variantError, setVariantError] = useState<string | null>(null);
  // Phase 56 Plan 56-04 (RECIPE-COMPOSITION-03/09) — validity gate wired up
  // from the modifier picker. Turns false when any OR-modifier line has
  // count > 0 but no chosen component; message names the FIRST offender so
  // the operator knows where to look. Extends the Submit-disable predicate
  // + surfaces the message as hint text near the Save button.
  const [modifierValidity, setModifierValidity] = useState<{
    isValid: boolean;
    message?: string;
  }>({ isValid: true });

  // Filter items by category's charging_model — only show items whose category
  // matches the modes's allowed charging models.
  const eligibleItems = useMemo(() => {
    const categoryIdsByModel = new Set(
      categories.filter((c) => cfg.chargingModels.includes(c.chargingModel)).map((c) => c.id),
    );
    return items.filter((it) => categoryIdsByModel.has(it.categoryId)).sort((a, b) => a.name.localeCompare(b.name));
  }, [items, categories, cfg.chargingModels]);

  const form = useForm<FormValues>({
    resolver: zodResolver(MemberStockOutSchema),
    defaultValues: {
      transactionDate: new Date(),
      itemId: undefined as unknown as number,
      quantity: 1,
      memberId: null,
      seatNumber: null,
      amountCharged: null,
    },
  });

  const watchedItemId = form.watch("itemId");
  const watchedQty = form.watch("quantity");
  const watchedMemberId = form.watch("memberId");
  const selectedItem = items.find((it) => it.id === watchedItemId);

  // Phase 6 — variant query for the selected item
  const { data: variants = [] } = useListItemVariants(
    watchedItemId ?? 0,
    undefined,
    {
      query: {
        queryKey: getListItemVariantsQueryKey(watchedItemId ?? 0),
        enabled: watchedItemId != null && watchedItemId > 0,
      },
    }
  );

  const hasVariants = variants.length > 0;
  const selectedVariant: InventoryItemVariant | undefined = variants.find((v) => v.id === variantId);

  // Reset variant + modifiers when item changes
  useEffect(() => {
    setVariantId(null);
    setAppliedModifiers([]);
    setLineTotal(null);
    setVariantError(null);
    setModifierValidity({ isValid: true });
  }, [watchedItemId]);

  // Phase 2.1 polish I — for With-Limit mode, fetch the member's entitlement
  // for the selected item to surface "X remaining" and warn on overage.
  const entitlementParams = {
    memberId: watchedMemberId ?? undefined,
    itemId: watchedItemId || undefined,
    activeOnly: true,
  };
  const { data: entitlements = [] } = useListMemberEntitlements(
    entitlementParams,
    {
      query: {
        enabled: mode === "with_limit" && !!watchedMemberId && !!watchedItemId,
        queryKey: getListMemberEntitlementsQueryKey(entitlementParams),
      },
    },
  );
  const entitlement = entitlements[0] ?? null;
  const quotaRemaining = entitlement ? Number(entitlement.quotaRemaining) : null;
  const overageQty = quotaRemaining != null && watchedQty > quotaRemaining
    ? watchedQty - quotaRemaining
    : 0;
  const overageRate = selectedItem ? Number((selectedItem as { overageRate?: string | number | null }).overageRate ?? 0) : 0;
  const projectedOverageCharge = overageQty > 0 && overageRate > 0 ? overageQty * overageRate : 0;

  // For Pay Per Use, default amount = item.selling_price × quantity (or lineTotal if modifiers applied)
  useEffect(() => {
    if (!cfg.showAmount || !selectedItem) return;
    const basePrice = Number((selectedItem as { sellingPrice?: string | number | null }).sellingPrice ?? (selectedItem as { overageRate?: string | number | null }).overageRate ?? 0);
    const effective = lineTotal != null ? lineTotal : basePrice * watchedQty;
    if (effective > 0) {
      form.setValue("amountCharged", Math.round(effective * 100) / 100);
    }
  }, [cfg.showAmount, selectedItem, watchedQty, lineTotal, form]);

  async function onSubmit(values: FormValues) {
    // VAR-02 guard: if item has variants, variantId must be set
    if (hasVariants && variantId == null) {
      setVariantError("Select a variant for this item");
      return;
    }
    setVariantError(null);

    try {
      await createMutation.mutateAsync({
        data: {
          transactionDate: values.transactionDate.toISOString(),
          consumptionType: cfg.consumptionType,
          itemId: values.itemId,
          quantityIssued: values.quantity,
          memberId: values.memberId ?? null,
          cabinNumber: values.seatNumber ?? null,
          amountCharged: cfg.showAmount ? (values.amountCharged ?? null) : null,
          // Phase 6 — variant + modifier passthrough
          // Phase 55 Plan 55-05: send the count-bearing shape when any modifier
          // is applied (any entry with count>0); null otherwise.
          variantId: variantId ?? null,
          appliedModifiers: appliedModifiers.length > 0 ? appliedModifiers : null,
        },
      });
      toast({ title: "Stock-out recorded." });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      onSaved?.();
    } catch (err: unknown) {
      // Handle 422 — recipe cascade insufficient stock (REC-04)
      const anyErr = err as { status?: number; data?: unknown };
      if (anyErr.status === 422) {
        const data = anyErr.data as {
          error?: string;
          details?: { componentName?: string; currentStock?: number; required?: number };
          variantDisplayName?: string;
          variantCurrentStock?: number;
        } | null;
        if (data?.details?.componentName != null) {
          toast({
            title: `Cannot fulfil: Need ${data.details.required} of ${data.details.componentName} but only ${data.details.currentStock} available.`,
            variant: "destructive",
          });
        } else if (data?.variantDisplayName != null) {
          toast({
            title: `Insufficient variant stock: ${data.variantDisplayName} has ${data.variantCurrentStock} left.`,
            variant: "destructive",
          });
        } else {
          toast({ title: data?.error ?? "Could not save. Please try again.", variant: "destructive" });
        }
      } else {
        toast({ title: "Could not save. Please try again.", variant: "destructive" });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-4 pt-2 pb-6 max-w-md">
        <div>
          <h2 className="text-lg font-semibold">{cfg.title}</h2>
          <p className="text-xs text-muted-foreground">{cfg.hint}</p>
        </div>

        <FormField
          name="transactionDate"
          control={form.control}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date <span className="text-destructive">*</span></FormLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Pick a date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(d) => { if (d) { field.onChange(d); setDatePickerOpen(false); } }}
                    disabled={(d) => d > new Date()}
                    className="p-4 [--cell-size:2.5rem]"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="itemId"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Item <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <ItemPicker
                  items={eligibleItems}
                  value={field.value ?? null}
                  onChange={(id) => field.onChange(id)}
                  placeholder={eligibleItems.length === 0 ? "No items in this charging model" : "Pick an item"}
                />
              </FormControl>
              {eligibleItems.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add items to a category whose charging model is {cfg.chargingModels.map((m) => m.replace(/_/g, " ")).join(" or ")}.
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Phase 6 — VAR-02 / VAR-03: Variant dropdown (required when item has variants) */}
        {hasVariants && watchedItemId > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Variant <span className="text-destructive">*</span>
            </label>
            <Select
              value={variantId != null ? String(variantId) : ""}
              onValueChange={(v) => {
                setVariantId(v ? Number(v) : null);
                setVariantError(null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a variant…" />
              </SelectTrigger>
              <SelectContent>
                {variants.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.displayName ?? `Variant #${v.id}`}
                    <span className="ml-2 text-xs text-muted-foreground">
                      (stock: {v.currentStock})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {variantError && (
              <p className="text-sm text-destructive">{variantError}</p>
            )}
            {selectedVariant && (
              <p className="text-xs text-muted-foreground">
                Selected: {selectedVariant.displayName} — stock: {selectedVariant.currentStock}
              </p>
            )}
          </div>
        )}

        <FormField
          name="quantity"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 1"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Phase 2.1 polish I — With-Limit quota indicator + overage warning */}
        {mode === "with_limit" && watchedMemberId && watchedItemId ? (
          entitlement == null ? (
            <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
              No active entitlement found for this member × item — saving will record consumption against quota with 0 remaining.
            </p>
          ) : overageQty > 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <p><b>Quota exceeded.</b> {quotaRemaining} remaining; logging {watchedQty} means {overageQty} over.</p>
                {overageRate > 0 ? (
                  <p>Overage at ₹{overageRate.toLocaleString("en-IN")}/{selectedItem?.unitOfMeasurement} = <b>₹{projectedOverageCharge.toLocaleString("en-IN")}</b> charge. Phase 3 will auto-bill this; for now record here and reconcile later.</p>
                ) : (
                  <p>No overage rate on this item — overage will be flagged but not auto-charged.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-emerald-700 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
              Quota: {quotaRemaining} remaining for this period — within limit.
            </p>
          )
        ) : null}

        {/* Phase 6 — modifier checkboxes (recipe items only) */}
        {/* Phase 56 Plan 56-04 — onValidityChange lets the picker signal
            OR-modifier missing-component so Submit can be disabled + a hint
            can be surfaced. */}
        <StockOutModifierCheckboxes
          key={watchedItemId ?? "no-item"}
          itemId={watchedItemId > 0 ? watchedItemId : null}
          basePrice={Number((selectedItem as { sellingPrice?: string | number | null } | undefined)?.sellingPrice ?? 0)}
          quantity={watchedQty ?? 1}
          appliedModifiers={appliedModifiers}
          onChange={setAppliedModifiers}
          onLineTotalChange={setLineTotal}
          onValidityChange={(isValid, message) =>
            setModifierValidity({ isValid, message })
          }
        />

        <div className="space-y-2 rounded-md border p-3">
          <p className="text-xs text-muted-foreground">Identify the consumer — pick a member <em>or</em> enter a seat / cabin number.</p>
          <FormField
            name="memberId"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Member</FormLabel>
                <FormControl>
                  <MemberCombobox value={field.value ?? null} onChange={(id) => field.onChange(id)} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="seatNumber"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Seat / Cabin Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. S-12 or C-3"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {cfg.showAmount && (
          <FormField
            name="amountCharged"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount Charged (₹)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Auto-derived from item rate × qty"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                  />
                </FormControl>
                {selectedItem ? (
                  <p className="text-xs text-muted-foreground">
                    {lineTotal != null
                      ? `Line total (with modifiers): ₹${lineTotal.toFixed(2)}`
                      : `Default rate: ₹${Number((selectedItem as { overageRate?: string | number | null }).overageRate ?? 0).toLocaleString("en-IN")} per ${selectedItem.unitOfMeasurement}`}
                  </p>
                ) : null}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Phase 56 Plan 56-04 — surface the OR-picker missing-component
            hint next to Submit so the operator knows why it's disabled. */}
        {!modifierValidity.isValid && modifierValidity.message && (
          <p className="text-xs text-destructive text-right">
            {modifierValidity.message}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onDiscard}>Discard</Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || !modifierValidity.isValid}
          >
            {createMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
